import { useMemo } from 'react';

import { ELIGIBILITY_APPLICATION_KEY } from '@/constants/treatmentGroups';
import useJurisdictions from '@/hooks/useJurisdictions';
import { useLogicApplication } from '@/hooks/useLogicApplication';
import { useLogicGroups } from '@/hooks/useLogicGroups';
import { getSelectedJurisdictionIds } from '@/utils/jurisdictionEligibility';

/**
 * The jurisdiction names selected by a treatment group's eligibility criteria,
 * resolved via the shared treatment_group_eligibility LogicApplication.
 */
const useTreatmentGroupJurisdictionNames = (treatmentGroupId?: string): string[] => {
  const { data: eligibilityApplication } = useLogicApplication(ELIGIBILITY_APPLICATION_KEY);
  const { data: rootGroups } = useLogicGroups(eligibilityApplication?.id, treatmentGroupId);
  const { data: jurisdictions } = useJurisdictions();

  const jurisdictionIds = useMemo(
    () => getSelectedJurisdictionIds(rootGroups ?? [], jurisdictions ?? []),
    [rootGroups, jurisdictions]
  );

  return useMemo(
    () =>
      jurisdictionIds
        .map((id) => jurisdictions?.find((jurisdiction) => jurisdiction.id === id)?.name)
        .filter((name): name is string => !!name),
    [jurisdictionIds, jurisdictions]
  );
};

export default useTreatmentGroupJurisdictionNames;
